import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Points } from './Points';
import {
  selectCurrentAnimation,
  selectIsAnimating,
  selectAnimationQueue,
  startAnimation,
  completeAnimation,
} from 'store/slices/pointsSlice';
import { useGetNotificationSettingsQuery } from 'store/services/message/messageApi';
import { playPointsSound } from 'utils/audioManager';
import './AnimatedPoints.css';

interface AnimatedPointsProps {
  points?: number;
  isLoading?: boolean;
  type?: 'default' | 'big' | 'small';
}

/**
 * Get animation duration based on change magnitude
 */
const getAnimationDuration = (change: number): number => {
  const absChange = Math.abs(change);
  if (absChange >= 1000) return 1200;
  if (absChange < 100) return 500;
  return 800;
};

/**
 * Cubic ease-out function for smooth deceleration
 */
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Format number with commas
 */
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Animated wrapper for the Points component
 * Handles counting animations, glow effects, and sound playback
 */
export const AnimatedPoints = ({
  points = 0,
  isLoading = false,
  type = 'default',
}: AnimatedPointsProps) => {
  const dispatch = useDispatch();

  // Redux selectors
  const currentAnimation = useSelector((state: RootState) =>
    selectCurrentAnimation(state)
  );
  const isAnimating = useSelector((state: RootState) =>
    selectIsAnimating(state)
  );
  const animationQueue = useSelector((state: RootState) =>
    selectAnimationQueue(state)
  );

  // Get user's sound settings
  const { data: notificationSettings } = useGetNotificationSettingsQuery();
  const soundEnabled = notificationSettings?.soundEnabled ?? true;

  // Local state
  const [displayValue, setDisplayValue] = useState(points);
  const [differential, setDifferential] = useState<number | null>(null);
  const [glowClass, setGlowClass] = useState('');

  // Refs for animation
  const animationRef = useRef<number | null>(null);
  const soundPlayedRef = useRef(false);
  const previousPointsRef = useRef(points);

  // Clean up animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Sync display value with points when not animating
  useEffect(() => {
    if (!isAnimating && !currentAnimation) {
      setDisplayValue(points);
      previousPointsRef.current = points;
    }
  }, [points, isAnimating, currentAnimation]);

  // Start next animation when queue has items and not currently animating
  useEffect(() => {
    if (!isAnimating && animationQueue.length > 0) {
      dispatch(startAnimation());
    }
  }, [isAnimating, animationQueue.length, dispatch]);

  // Process current animation
  useEffect(() => {
    if (!currentAnimation) return;

    const { newBalance, change } = currentAnimation;
    const duration = getAnimationDuration(change);
    const startValue = previousPointsRef.current;
    const startTime = performance.now();

    // Reset sound played flag for this animation
    soundPlayedRef.current = false;

    // Show differential and glow - always use gold (positive) glow
    // Even debits (like trade_initiated) are positive experiences for users
    setDifferential(change);
    setGlowClass('points-glow-positive');

    // Play sound at the start of animation
    if (!soundPlayedRef.current) {
      playPointsSound(change, soundEnabled);
      soundPlayedRef.current = true;
    }

    // Animation loop
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOut(progress);

      // Calculate current display value
      const currentValue = Math.round(
        startValue + (newBalance - startValue) * easedProgress
      );
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - show differential for additional 0.5s
        animationRef.current = null;
        previousPointsRef.current = newBalance;

        setTimeout(() => {
          setDifferential(null);
          setGlowClass('');
          dispatch(completeAnimation());
        }, 500);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [currentAnimation, dispatch, soundEnabled]);

  return (
    <div className={`animated-points-wrapper ${glowClass}`}>
      <Points points={displayValue} isLoading={isLoading} type={type} />
      {differential !== null && (
        <span className="points-differential">
          {differential > 0 ? '+' : ''}
          {formatNumber(differential)}
        </span>
      )}
    </div>
  );
};

export default AnimatedPoints;
